import type { UserConsentId } from '@paid-ui/enums/consent';
import type { FeatureItem } from '@paid-ui/enums/feature';
import type { ConsentItem } from '@paid-ui/models/app-config';
import type { Address } from '@paid-ui/schemas/zod/address';
import type {
  GroupRelationGroup,
  GroupUser,
  LinkedBusiness,
  UserAccount,
  UserBusiness,
  UserConsent,
  UserDetail,
  UserInfoForBusiness,
  UserSummary,
} from '@paid-ui/types';
import type { UserSignatory, UserSignature } from '@paid-ui/types/signatory';

import { uniqBy } from 'lodash';
import { proxy } from 'valtio';
import { devtools, proxyMap } from 'valtio/utils';

import {
  BackgroundCheckStatus,
  TransactionAccountMatcherType,
  TransactionAccountStatus,
  TransactionAccountType,
} from '@paid-ui/enums/account';
import { BusinessStructure } from '@paid-ui/enums/business';
import { UserConsentType } from '@paid-ui/enums/consent';
import { PartyType } from '@paid-ui/enums/party';
import { SignatureType } from '@paid-ui/enums/signatory';
import { CompanyDirectorsStructure, UserGroupRelation, UserGroupType } from '@paid-ui/enums/user';
import { browserEnvs } from '@paid-ui/env';
import { resetProxy } from '@paid-ui/utils/reset-proxy';

import { retentionAccountManager } from './retention-account';
import { savingsAccountManager } from './savings-account';
import { transactionAccountManager } from './transaction-account';

const { blacklistGroupIds, blacklistUserIds } = browserEnvs;

export interface UserConsentEntry {
  sourceId: UserConsentId;
  sourceLink: string;
  consentItem: UserConsentType;
  version: string;
  isUserAccepted: boolean;
}

const initialState = {
  /** If user signed in */
  authed: false,
  /** Token saved */
  token: '',
  /** Impersonation */
  impersonation: false,
  /** User profile mapped */
  profile: undefined as UserDetail | undefined,
  /** User business mapped */
  business: undefined as UserBusiness | undefined,
  /** Trustee business when current group is a trust with company trustee */
  trust: undefined as LinkedBusiness | undefined,
  /** User account mapped */
  account: undefined as UserAccount | undefined,
  /** User accounts */
  accounts: [] as UserAccount[],
  /** User account set */
  accountSet: new Set(),
  /** Features that is active for user */
  features: [] as FeatureItem[],
  /** If current user is owner */
  isOwner: false,
  /** If current user has business */
  hasBusiness: false,
  /** If current user is individuals */
  isIndividuals: false,
  /** If current user is trustee */
  isTrust: false,
  /** If current user is individual trustee */
  isIndividualTrust: false,
  /** Formatted name for display */
  displayName: '',
  /** Business structure of current business */
  businessStructure: undefined as BusinessStructure | undefined,
  /** Registered name of current user */
  registeredName: '',
  /** Registered address of current user */
  registeredAddress: undefined as Address | undefined,
  /** If registered for GST or not */
  registeredForGst: true,
  /** ABN of current business */
  abn: '',
  /** ACN of current company */
  acn: '',
  /** Contact number of business */
  contactNumber: '',
  /** Builder licence number */
  builderLicenceNumber: '',
  /** Early release discount rate for current user */
  earlyReleaseDiscountRate: undefined as number | undefined,
  /** If current user can create project and account or not */
  provideBuildingServices: false,
  /** If current user's business setup is completed */
  businessSetupCompleted: false,
  /** If current user's transaction account is provisioned */
  provisioned: false,
  /** KYC status of current user */
  kycStatus: BackgroundCheckStatus.UNKNOWN,
  /** KYB status of current user */
  kybStatus: BackgroundCheckStatus.UNKNOWN,
  /** Background check status of current user */
  backgroundCheckStatus: BackgroundCheckStatus.UNKNOWN,
  /**
   * When the account application was raised — the earliest of the KYC and KYB check
   * creation times. Null until a check exists.
   */
  accountAppliedAt: null as string | null,
  /** If current user's transaction account is ready */
  isAccountReady: false,
  /**
   * If current user has a transaction account worth showing. An internal
   * payments only account exists and holds funds without passing KYC, so it is
   * viewable while `isAccountReady` stays false and keeps transfers, savings
   * and the apply flow gated.
   */
  isAccountViewable: false,
  /** If current user's transaction account is closed */
  isAccountClosed: false,
  /** If current user's transaction account is suspended */
  isAccountSuspended: false,
  /** If current user's transaction account is applied or not */
  isAccountApplied: false,
  /** If background check or account creation in progress or not */
  isAccountRequestPending: false,
  /** If background check or account creation failed or not */
  isAccountRequestFailed: false,
  /** If Kyc process requested or not (requested, pass, result_ready, failed) */
  isKycRequested: false,
  /** If Kyc process requesting or not (requested, pass, result_ready) */
  isKycNoFailRequested: false,
  /** If Kyb process requested without fail or not */
  isKybRequested: false,
  /** If Kyb process requested without fail or not */
  isKybNoFailRequested: false,
  /** If Kyc/Kyb process requested or not */
  isKybKycRequested: false,
  /** If Kyc/Kyb process requested without fail or not */
  isKybKycNoFailRequested: false,
  /** Default view as of current user */
  defaultViewAs: PartyType.PAYER,
  /** Has multiple users */
  hasMultipleUsers: false,
  /** All additional business users including me */
  businessUsers: [] as UserInfoForBusiness[],
  /** Me in additional business users */
  currentUser: undefined as UserInfoForBusiness | undefined,
  /** Additional business users excluding me */
  additionalUsers: [] as UserInfoForBusiness[],
  /** User profile requires edit */
  requirePersonalEdit: false,
  /** Business profile requires edit */
  requireBusinessEdit: false,
  /** Business profile requires edit for primary contract */
  requireBusinessEditForPrime: false,
  /** Business profile requires additional users */
  requireAdditionalUsersForBusiness: false,
  /** Trustee profile requires edit */
  requireTrusteeEdit: false,
  /** Trustee profile requires additional users */
  requireAdditionalUsersForTrustee: false,
  /** Force update flag */
  forceUpdate: 0,
  /** Show retention account flag */
  retentionEnabled: false,
  /** User group id */
  userGroupId: '',
  /** Trustee business id when current group is a trust with company trustee */
  trusteeBusinessId: '',
  /** User group relation */
  userGroupRelation: UserGroupRelation.UNSPECIFIED,

  /** User signatures */
  signature: null as UserSignature | null,
  /** Initial version of user signature */
  initialSignature: null as UserSignature | null,
  /** All signatories */
  signatories: [] as UserSignatory[],
  /** User group ID and signatories mapping */
  signatoriesMap: proxyMap<string, UserSignatory[]>(),
  /** Required number of signatories */
  requiredGroupSignatories: 0,
  /** Group relation */
  groups: [] as GroupRelationGroup[],
  /** If the accounts still loading */
  isAccountsLoading: true,
  // Flags for funders
  isFunder: false,
  hidePermissions: false,
  disableContractActions: false,
  /** Computed user consents (source × item) with acceptance status */
  consents: [] as UserConsentEntry[],
};

export const userManager = proxy(initialState);

devtools(userManager, {
  name: 'User',
  enabled: false,
});

export const signIn = (token: string) => {
  userManager.authed = true;
  userManager.token = token;
};

export const requestedCheckStatus = new Set([
  BackgroundCheckStatus.REQUESTED,
  BackgroundCheckStatus.RESULT_READY,
  BackgroundCheckStatus.PASS,
  BackgroundCheckStatus.FAIL,
]);

export const noFailRequestedCheckStatus = new Set([
  BackgroundCheckStatus.REQUESTED,
  BackgroundCheckStatus.RESULT_READY,
  BackgroundCheckStatus.PASS,
]);

const pendingCheckStatus = new Set([
  BackgroundCheckStatus.REQUESTED,
  BackgroundCheckStatus.RESULT_READY,
]);

const toFixed = (value?: number) => (value ? Number((value * 100).toFixed(2)) : undefined);

export const getUserSignatory = (
  user: GroupUser | UserSummary,
  isCurrentUser: boolean,
): UserSignatory => ({
  id: user.id || null,
  email: user.email,
  mobile: user.mobile ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
  fullName: [user.firstName, user.lastName].join(' '),
  relation: user.relation ?? UserGroupRelation.UNSPECIFIED,
  isCurrentUser,
  selected: user.groupDefaultSignatory ?? false,
  status: user.status,
});

export const getUserSignatories = (profile?: UserDetail) => {
  const usersInGroup = profile?.groupRelation?.group?.users ?? [];
  return usersInGroup
    .filter((user) => user.relation !== UserGroupRelation.EMPLOYEE)
    .map((user) => getUserSignatory(user, user.email === profile?.email));
};

const getUserSignatoriesMap = (profile?: UserDetail) => {
  const groups =
    uniqBy(
      profile?.groupRelations?.map((relation) => relation.group),
      'id',
    ) ?? [];
  return new Map(
    groups.map((group) => {
      const users = group.users ?? [];
      const signatories = users
        .filter((user) => user.relation !== UserGroupRelation.EMPLOYEE)
        .map((user) => getUserSignatory(user, user.email === profile?.email));
      return [group.id, signatories];
    }),
  );
};

/**
 * The earliest of the given dates, ignoring missing and unparseable ones. Returns the
 * original string rather than a parsed value, so the caller keeps whatever precision the
 * API sent.
 */
const getEarliestDate = (...dates: (string | undefined)[]) => {
  const parsed = dates
    .flatMap((date) => (date ? [{ date, time: new Date(date).getTime() }] : []))
    .filter(({ time }) => Number.isFinite(time))
    .sort((a, b) => a.time - b.time);

  return parsed[0]?.date ?? null;
};

export const saveProfile = (profile: UserDetail) => {
  const {
    firstName,
    lastName,
    mobile,
    homeAddress,
    impersonation,
    backgroundCheck,
    features = [],
    displayName: individualDisplayName,
    retentionEnabled,
    groupRelation,
    userSignatures,
  } = profile;

  const business = groupRelation?.group.business;
  const kycCheckStatus = backgroundCheck?.status;
  const kybCheckStatus = business?.backgroundCheck?.status;

  // Whichever check was raised first is when the user actually applied — a business
  // has both, and they are not created together.
  const accountAppliedAt = getEarliestDate(
    backgroundCheck?.createdAt,
    business?.backgroundCheck?.createdAt,
  );
  const isTrust = business?.businessStructure === BusinessStructure.TRUST;

  const trusteeRelation = business?.linkedBusinesses?.find(
    (lb) => lb.relation === UserGroupRelation.TRUSTEE,
  );
  const trust = isTrust ? trusteeRelation?.business : undefined;
  const hasMultipleDirector = isTrust
    ? trust?.companyDirectorsStructure === CompanyDirectorsStructure.MULTI_DIRECTORS
    : business?.companyDirectorsStructure === CompanyDirectorsStructure.MULTI_DIRECTORS;
  const isOwner = !business?.provideBuildingServices;
  const hasBusiness = Boolean(business);

  // Presence of a linked trustee business marks a company trust; absence marks
  // an individual trust.
  const isIndividualTrust = isTrust && !business?.linkedBusinesses?.length;
  const displayName = groupRelation?.group.displayName ?? individualDisplayName;
  const businessStructure = business?.businessStructure ?? BusinessStructure.OTHER;
  const registeredName = business?.registeredName ?? '';
  const registeredAddress = business?.registeredAddress;
  const registeredForGst = business?.registeredForGst ?? true;
  const contactNumber = business?.contactNumber ?? '';
  const abn = business?.abn ?? '';
  const builderLicenceNumber = business?.builderLicenceNumber ?? '';
  const earlyReleaseDiscountRate = toFixed(business?.earlyReleaseDiscountRate);
  const provideBuildingServices = business?.provideBuildingServices ?? false;
  const businessSetupCompleted = business?.businessSetupCompleted ?? false;
  const acn = (isTrust ? trust?.acn : business?.acn) ?? '';

  const isKycRequested = !!kycCheckStatus && requestedCheckStatus.has(kycCheckStatus);
  const isKycNoFailRequested = !!kycCheckStatus && noFailRequestedCheckStatus.has(kycCheckStatus);
  const isKybRequested = !!kybCheckStatus && requestedCheckStatus.has(kybCheckStatus);
  const isKybNoFailRequested = !!kybCheckStatus && noFailRequestedCheckStatus.has(kybCheckStatus);
  const isKybKycRequested = hasBusiness ? isKycRequested || isKybRequested : isKycRequested;
  const isKybKycNoFailRequested = hasBusiness
    ? isKycNoFailRequested || isKybNoFailRequested
    : isKycNoFailRequested;
  const backgroundCheckStatus = kybCheckStatus ?? kycCheckStatus ?? BackgroundCheckStatus.UNKNOWN;

  const defaultViewAs = isOwner ? PartyType.PAYER : PartyType.PAYEE;
  const groupUsers = groupRelation?.group?.users ?? [];

  const currentUser = groupUsers.find((user) => user.id === profile.id);
  const additionalGroupUsers = groupUsers?.filter((user) => user.id !== profile.id);
  const additionals = additionalGroupUsers;
  const officers = groupUsers.filter((user) => user.relation !== UserGroupRelation.EMPLOYEE);
  const requiredGroupSignatories = groupRelation?.group.requiredGroupSignatories ?? 1;

  userManager.signature =
    userSignatures.find((s) => s.signatureType === SignatureType.FULL_NAME) ?? null;
  userManager.initialSignature =
    userSignatures.find((s) => s.signatureType === SignatureType.INITIAL) ?? null;

  userManager.signatories = getUserSignatories(profile);
  userManager.signatoriesMap = getUserSignatoriesMap(profile);
  userManager.requiredGroupSignatories = requiredGroupSignatories;

  userManager.impersonation = impersonation ?? false;
  userManager.hasMultipleUsers = hasMultipleDirector;
  userManager.isKycRequested = isKycRequested;
  userManager.isKycNoFailRequested = isKycNoFailRequested;
  userManager.isKybRequested = isKybRequested;
  userManager.isKybNoFailRequested = isKybNoFailRequested;
  userManager.userGroupId = groupRelation?.group.id ?? '';
  userManager.trusteeBusinessId = trust?.id ?? '';
  userManager.userGroupRelation = groupRelation?.relation ?? UserGroupRelation.UNSPECIFIED;
  userManager.profile = profile;
  userManager.business = business;
  userManager.trust = trust;
  userManager.features = features.map(({ name }) => name);
  userManager.isOwner = isOwner;
  userManager.hasBusiness = hasBusiness;
  userManager.isTrust = isTrust;
  userManager.isIndividuals = groupRelation?.group.type === UserGroupType.INDIVIDUALS;
  userManager.isIndividualTrust = isIndividualTrust;
  userManager.displayName = displayName;
  userManager.businessStructure = businessStructure;
  userManager.registeredName = registeredName;
  userManager.registeredAddress = registeredAddress;
  userManager.registeredForGst = registeredForGst;
  userManager.contactNumber = contactNumber;
  userManager.abn = abn;
  userManager.acn = acn;
  userManager.builderLicenceNumber = builderLicenceNumber;
  userManager.earlyReleaseDiscountRate = earlyReleaseDiscountRate;
  userManager.provideBuildingServices = provideBuildingServices;
  userManager.businessSetupCompleted = businessSetupCompleted;
  userManager.kybStatus = kybCheckStatus ?? BackgroundCheckStatus.UNKNOWN;
  userManager.kycStatus = kycCheckStatus ?? BackgroundCheckStatus.UNKNOWN;
  userManager.backgroundCheckStatus = backgroundCheckStatus;
  userManager.accountAppliedAt = accountAppliedAt;
  userManager.isKybKycRequested = isKybKycRequested;
  userManager.isKybKycNoFailRequested = isKybKycNoFailRequested;
  userManager.defaultViewAs = defaultViewAs;
  userManager.businessUsers = groupUsers;
  userManager.currentUser = currentUser;
  userManager.additionalUsers = additionals;
  userManager.retentionEnabled = !!retentionEnabled;
  userManager.requirePersonalEdit = !firstName || !lastName || !mobile || !homeAddress;
  userManager.requireBusinessEdit = hasBusiness ? !business?.businessSetupCompleted : false;
  userManager.requireBusinessEditForPrime = hasBusiness
    ? provideBuildingServices && !builderLicenceNumber
    : false;
  userManager.requireTrusteeEdit =
    isTrust && !isIndividualTrust ? !trust?.businessSetupCompleted : false;
  userManager.requireAdditionalUsersForBusiness = hasBusiness
    ? officers.length < requiredGroupSignatories
    : false;
  userManager.requireAdditionalUsersForTrustee =
    isTrust && !isIndividualTrust ? officers.length < requiredGroupSignatories : false;

  const groups = profile.groupRelations?.map((relation) => relation.group) ?? [];
  userManager.groups = groups;

  const isFunderUser = !!profile.id && blacklistUserIds.split(',').includes(profile.id);
  const isFunderGroup =
    !!groupRelation?.group.id && blacklistGroupIds.split(',').includes(groupRelation.group.id);

  userManager.isFunder = isFunderUser;
  userManager.hidePermissions = impersonation ? false : isFunderGroup;
  userManager.disableContractActions = isFunderUser;
};

export const saveConsents = (
  profileConsents: UserConsent[] = [],
  configConsentItems: Record<UserConsentType, ConsentItem> = {} as Record<
    UserConsentType,
    ConsentItem
  >,
) => {
  const entries: UserConsentEntry[] = [];

  for (const [key, item] of Object.entries(configConsentItems)) {
    const consentItem = key as UserConsentType;
    if (consentItem === UserConsentType.PAID_BACKGROUND_CHECK) continue;
    const accepted = profileConsents.find((c) => c.consentItem === consentItem);
    const isUserAccepted = accepted?.version === item.version;

    for (const source of item.source) {
      entries.push({
        sourceId: source.id,
        sourceLink: source.link,
        consentItem,
        version: item.version,
        isUserAccepted,
      });
    }
  }

  userManager.consents = entries;
};

export const resetUserManager = () => {
  resetProxy(userManager, initialState);
};

export const saveTransactionAccounts = (accounts: UserAccount[]) => {
  const account = accounts.find((acc) => acc.accountType === TransactionAccountType.STANDARD);
  const { hasBusiness, kybStatus, kycStatus } = userManager;
  const provisioned = account?.provisioned ?? false;
  const accountStatus = account?.status;

  const getIsAccountRequestPending = () => {
    const isKycPending = pendingCheckStatus.has(kycStatus);
    const isKybPending = pendingCheckStatus.has(kybStatus);

    if (!hasBusiness) {
      return isKycPending;
    }

    if (isKycPending) {
      return noFailRequestedCheckStatus.has(kybStatus);
    }

    if (isKybPending) {
      return noFailRequestedCheckStatus.has(kycStatus);
    }

    if (account) {
      return !provisioned;
    }

    return false;
  };

  const getIsAccountReady = () => {
    const isKycPass = kycStatus === BackgroundCheckStatus.PASS;
    const isKybPass = kybStatus === BackgroundCheckStatus.PASS;

    // Passing checks are not an account. `accountStatus` is undefined until one exists,
    // and `undefined !== NOT_READY` is true — so without this guard the window between
    // the checks passing and the account being provisioned read as ready, landing the
    // user on an empty accounts overview with no way to apply.
    if (!account) {
      return false;
    }

    if (!hasBusiness) {
      return isKycPass && accountStatus !== TransactionAccountStatus.NOT_READY;
    }

    return isKycPass && isKybPass && accountStatus !== TransactionAccountStatus.NOT_READY;
  };

  const isAccountRequestPending = getIsAccountRequestPending();
  const isAccountRequestFailed = hasBusiness
    ? kycStatus === BackgroundCheckStatus.FAIL || kybStatus === BackgroundCheckStatus.FAIL
    : kycStatus === BackgroundCheckStatus.FAIL;
  const isAccountApplied = hasBusiness
    ? kycStatus !== BackgroundCheckStatus.UNKNOWN && kybStatus !== BackgroundCheckStatus.UNKNOWN
    : kycStatus !== BackgroundCheckStatus.UNKNOWN;
  const isAccountClosed = accountStatus === TransactionAccountStatus.CLOSED;
  const isAccountSuspended = accountStatus === TransactionAccountStatus.SUSPENDED;
  const isAccountReady = getIsAccountReady();
  const isAccountViewable = isAccountReady || Boolean(account?.internalPaymentsOnly);

  if (account && isAccountViewable) {
    const matchers = account?.matchers ?? [];
    const mainAccount = matchers.find(
      (matcher) => matcher.type === TransactionAccountMatcherType.MAIN,
    );
    if (mainAccount) {
      transactionAccountManager.id = mainAccount.id;
      transactionAccountManager.accountId = account.id;
      transactionAccountManager.internalPaymentsOnly = Boolean(account.internalPaymentsOnly);
      transactionAccountManager.bsb = mainAccount.bsb ?? '';
      transactionAccountManager.accountNumber = mainAccount.accountNumber ?? '';
    }

    const retentionAccount = matchers.find(
      (matcher) => matcher.type === TransactionAccountMatcherType.RETENTION,
    );
    if (retentionAccount) {
      retentionAccountManager.id = retentionAccount.id;
      retentionAccountManager.accountId = account.id;
      retentionAccountManager.bsb = retentionAccount.bsb ?? '';
      retentionAccountManager.accountNumber = retentionAccount.accountNumber ?? '';
    }

    const savingsAccount = accounts.find(
      (acc) => acc.accountType === TransactionAccountType.SAVINGS,
    );

    if (savingsAccount) {
      const matcher = savingsAccount.matchers?.find(
        (matcher) => matcher.type === TransactionAccountMatcherType.MAIN,
      );
      if (matcher) {
        savingsAccountManager.id = matcher.id;
        savingsAccountManager.accountId = savingsAccount.id;
        savingsAccountManager.internalPaymentsOnly = Boolean(savingsAccount.internalPaymentsOnly);
        savingsAccountManager.bsb = matcher.bsb ?? '';
        savingsAccountManager.accountNumber = matcher.accountNumber ?? '';
      }
    }
  }

  userManager.account = account;
  userManager.provisioned = provisioned;
  userManager.accounts = accounts;
  userManager.accountSet = new Set(accounts.map((account) => account.id));
  userManager.isAccountApplied = isAccountApplied;
  userManager.isAccountRequestPending = isAccountRequestPending;
  userManager.isAccountRequestFailed = isAccountRequestFailed;
  userManager.isAccountReady = isAccountReady;
  userManager.isAccountViewable = isAccountViewable;
  userManager.isAccountClosed = isAccountClosed;
  userManager.isAccountSuspended = isAccountSuspended;
  userManager.isAccountsLoading = false;
};

export const reloadProfile = () => {
  const { profile, forceUpdate } = userManager;
  if (!profile) return;
  userManager.forceUpdate = forceUpdate > 100 ? 1 : forceUpdate + 1;
};
